import {
  CUBE_SIZE,
  LOOP_CHANCE,
  ONE_WAY_DOORS,
  SPAWN_PATH_MAX,
  SPAWN_PATH_MIN,
  SPAWN_SEPARATION_MIN,
} from './config.js';
import { createRng, randomInt, shuffle } from './rng.js';
import { DIRECTIONS, subtract, type Vec3 } from './vec.js';

export interface MazeOptions {
  size?: number;
  loopChance?: number;
  /** One-way doors to create (only ever on loop doors, so every room stays reachable). */
  oneWayDoors?: number;
}

/**
 * A cube of rooms joined by doors. Generated from a random spanning tree (so every room
 * is reachable) plus a few extra doors that create loops. The same seed always produces
 * the same cube, core and spawns, which is what makes challenge links work.
 */
/** A maze without its secrets: see `Maze.toWire`. */
export interface MazeWire {
  size: number;
  doors: number[];
  oneWay: [number, number][];
  spawn: number;
}

export class Maze {
  readonly seed: number;
  readonly size: number;
  readonly roomCount: number;
  /** The target core's room. Only the rules engine and spectators may read this. */
  readonly core: number;
  /** Two spawn rooms at equal maze-path distance from the core. */
  readonly spawns: readonly [number, number];
  private readonly doors: Set<number>;
  /** One-way doors: door key → the room you may pass *from*. */
  private readonly oneWay: Map<number, number>;

  private constructor(
    seed: number,
    size: number,
    doors: Set<number>,
    core: number,
    spawns: [number, number],
    oneWay: Map<number, number> = new Map(),
  ) {
    this.seed = seed;
    this.size = size;
    this.roomCount = size * size * size;
    this.doors = doors;
    this.oneWay = oneWay;
    this.core = core;
    this.spawns = spawns;
  }

  /**
   * The maze as sent to an online player: doors and one-way doors, plus only that player's own
   * spawn. The core room and the rival's spawn stay on the server; players learn the core's
   * position only when it comes into view.
   */
  toWire(spawnSeat: 0 | 1): MazeWire {
    return {
      size: this.size,
      doors: [...this.doors],
      oneWay: [...this.oneWay],
      spawn: this.spawns[spawnSeat],
    };
  }

  /** Rebuilds a player's maze from `toWire`. Its `core` is -1 (unknown) and the rival spawn is -1. */
  static fromWire(w: MazeWire): Maze {
    return new Maze(-1, w.size, new Set(w.doors), -1, [w.spawn, -1], new Map(w.oneWay));
  }

  static generate(seed: number, options: MazeOptions = {}): Maze {
    const size = options.size ?? CUBE_SIZE;
    const loopChance = options.loopChance ?? LOOP_CHANCE;
    const rng = createRng(seed);
    const count = size * size * size;
    const shape = new Maze(seed, size, new Set(), 0, [0, 0]);

    // Randomized Kruskal over all adjacent pairs.
    const parent = new Int32Array(count);
    for (let i = 0; i < count; i++) parent[i] = i;
    const find = (a: number): number => {
      while (parent[a] !== a) {
        parent[a] = parent[parent[a]!]!;
        a = parent[a]!;
      }
      return a;
    };
    const edges: [number, number][] = [];
    for (let room = 0; room < count; room++) {
      for (const dir of [DIRECTIONS[0]!, DIRECTIONS[2]!, DIRECTIONS[4]!]) {
        const next = shape.neighbor(room, dir);
        if (next >= 0) edges.push([room, next]);
      }
    }
    shuffle(rng, edges);
    const doors = new Set<number>();
    const leftovers: [number, number][] = [];
    for (const [a, b] of edges) {
      const ra = find(a);
      const rb = find(b);
      if (ra !== rb) {
        parent[ra] = rb;
        doors.add(doorKey(a, b));
      } else {
        leftovers.push([a, b]);
      }
    }
    const loops: [number, number][] = [];
    for (const [a, b] of leftovers) {
      if (rng() < loopChance) {
        doors.add(doorKey(a, b));
        loops.push([a, b]);
      }
    }

    // A few loop doors become one-way. The spanning tree stays two-way, so every room is
    // still reachable from every other; one-way doors only remove shortcuts in one direction.
    const oneWay = new Map<number, number>();
    shuffle(rng, loops);
    for (const [a, b] of loops.slice(0, options.oneWayDoors ?? ONE_WAY_DOORS)) oneWay.set(doorKey(a, b), rng() < 0.5 ? a : b);

    const withDoors = new Maze(seed, size, doors, 0, [0, 0], oneWay);
    const placement = placeCoreAndSpawns(withDoors, rng);
    return new Maze(seed, size, doors, placement.core, placement.spawns, oneWay);
  }

  coords(room: number): Vec3 {
    const s = this.size;
    return [room % s, Math.floor(room / s) % s, Math.floor(room / (s * s))];
  }

  roomAt(x: number, y: number, z: number): number {
    const s = this.size;
    if (x < 0 || y < 0 || z < 0 || x >= s || y >= s || z >= s) return -1;
    return x + y * s + z * s * s;
  }

  /** The adjacent room in `dir`, or -1 past the cube's outer wall. */
  neighbor(room: number, dir: Vec3): number {
    const [x, y, z] = this.coords(room);
    return this.roomAt(x + dir[0], y + dir[1], z + dir[2]);
  }

  /** Whether a door exists between adjacent rooms (in either direction). */
  hasDoor(a: number, b: number): boolean {
    return this.doors.has(doorKey(a, b));
  }

  /** For a one-way door, the room it can be passed *from*; null for an ordinary door. */
  oneWayFrom(a: number, b: number): number | null {
    return this.oneWay.get(doorKey(a, b)) ?? null;
  }

  /** Whether you can pass from `a` into adjacent room `b` (a door exists, and it isn't one-way against you). */
  canPass(a: number, b: number): boolean {
    if (!this.hasDoor(a, b)) return false;
    const from = this.oneWayFrom(a, b);
    return from === null || from === a;
  }

  /** The room through the door in `dir`, or -1 when that wall is solid or one-way against you. */
  through(room: number, dir: Vec3): number {
    const next = this.neighbor(room, dir);
    return next >= 0 && this.canPass(room, next) ? next : -1;
  }

  openDirections(room: number): Vec3[] {
    return DIRECTIONS.filter((dir) => this.through(room, dir) >= 0);
  }

  /** The unit direction from `a` to an adjacent room `b`. */
  directionBetween(a: number, b: number): Vec3 {
    return subtract(this.coords(b), this.coords(a));
  }

  /** Straight-line distance in rooms, ignoring walls (what sensors measure). */
  straightDistance(a: number, b: number): number {
    const p = this.coords(a);
    const q = this.coords(b);
    return Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]);
  }

  /** Door-count distance from `start` to every room (-1 if unreachable). */
  pathDistances(start: number): Int32Array {
    const dist = new Int32Array(this.roomCount).fill(-1);
    dist[start] = 0;
    const queue = [start];
    for (let head = 0; head < queue.length; head++) {
      const room = queue[head]!;
      for (const dir of DIRECTIONS) {
        const next = this.through(room, dir);
        if (next >= 0 && dist[next] === -1) {
          dist[next] = dist[room]! + 1;
          queue.push(next);
        }
      }
    }
    return dist;
  }

  /** Door-count distance from every room *to* `target`, respecting one-way doors (-1 if it can't get there). */
  pathDistancesTo(target: number): Int32Array {
    const dist = new Int32Array(this.roomCount).fill(-1);
    dist[target] = 0;
    const queue = [target];
    for (let head = 0; head < queue.length; head++) {
      const room = queue[head]!;
      for (const dir of DIRECTIONS) {
        const prev = this.neighbor(room, dir);
        if (prev >= 0 && dist[prev] === -1 && this.canPass(prev, room)) {
          dist[prev] = dist[room]! + 1;
          queue.push(prev);
        }
      }
    }
    return dist;
  }

  /** Rooms within `depth` doors of any start room (what can be seen through doorways). */
  roomsWithin(starts: readonly number[], depth: number): Set<number> {
    const seen = new Map<number, number>();
    const queue: number[] = [];
    for (const s of starts) {
      seen.set(s, 0);
      queue.push(s);
    }
    for (let head = 0; head < queue.length; head++) {
      const room = queue[head]!;
      const d = seen.get(room)!;
      if (d >= depth) continue;
      for (const dir of DIRECTIONS) {
        const next = this.through(room, dir);
        if (next >= 0 && !seen.has(next)) {
          seen.set(next, d + 1);
          queue.push(next);
        }
      }
    }
    return new Set(seen.keys());
  }

  /** First direction along a shortest door path from `from` to `to`, or null if already there. */
  firstStepToward(from: number, to: number): Vec3 | null {
    if (from === to) return null;
    const prev = new Int32Array(this.roomCount).fill(-1);
    prev[from] = from;
    const queue = [from];
    for (let head = 0; head < queue.length; head++) {
      const room = queue[head]!;
      if (room === to) break;
      for (const dir of DIRECTIONS) {
        const next = this.through(room, dir);
        if (next >= 0 && prev[next] === -1) {
          prev[next] = room;
          queue.push(next);
        }
      }
    }
    if (prev[to] === -1) return null;
    let cur = to;
    while (prev[cur] !== from) cur = prev[cur]!;
    return this.directionBetween(from, cur);
  }
}

export function doorKey(a: number, b: number): number {
  return a < b ? a * 1_000_003 + b : b * 1_000_003 + a;
}

function placeCoreAndSpawns(maze: Maze, rng: () => number): { core: number; spawns: [number, number] } {
  for (let attempt = 0; attempt < 80; attempt++) {
    const core = randomInt(rng, maze.roomCount);
    const fromCore = maze.pathDistancesTo(core); // how far each room is from reaching the core
    const byDistance = new Map<number, number[]>();
    fromCore.forEach((d, room) => {
      if (d >= SPAWN_PATH_MIN && d <= SPAWN_PATH_MAX) {
        const list = byDistance.get(d) ?? [];
        list.push(room);
        byDistance.set(d, list);
      }
    });
    const distances = shuffle(rng, [...byDistance.keys()]);
    for (const d of distances) {
      const rooms = byDistance.get(d)!;
      if (rooms.length < 2) continue;
      const a = rooms[randomInt(rng, rooms.length)]!;
      const fromA = maze.pathDistances(a);
      let best = -1;
      let bestDistance = -1;
      for (const b of rooms) {
        if (b !== a && fromA[b]! > bestDistance) {
          bestDistance = fromA[b]!;
          best = b;
        }
      }
      if (bestDistance >= SPAWN_SEPARATION_MIN) {
        return rng() < 0.5 ? { core, spawns: [a, best] } : { core, spawns: [best, a] };
      }
    }
  }
  throw new Error(`Could not place a fair core and spawns for seed ${maze.seed}`);
}
